import styled, { css } from 'styled-components';
import { EllipsisPopout as _EllipsisPopout, Icon } from 'src/components';

export const Container = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    padding: ${theme.sz.s6} ${theme.sz.s10};
    width: 100%;
    margin: 0 auto;
    position: relative;
  `,
);

export const Layout = styled.div(
  ({ theme }) => css`
    display: flex;
    width: 100%;
    gap: ${theme.sz.s6};
    position: relative;

    @media (max-width: ${theme.sz.xl}) {
      flex-direction: column;
    }
  `,
);

export const VideoColumn = styled.div(
  ({ theme }) => css`
    flex: 0 0 45%;
    max-width: 45%;
    min-width: 0;
    order: 0;

    @media (max-width: ${theme.sz.xl}) {
      flex: 1 1 auto;
      max-width: 100%;
      width: 100%;
    }
  `,
);

export const ContentColumn = styled.div(
  ({ theme }) => css`
    flex: 1;
    order: 1;
    display: flex;
    flex-direction: column;
    position: relative;

    @media (max-width: ${theme.sz.xl}) {
      order: 1;
    }
  `,
);

export const CountersContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.sz.s2};
    margin-bottom: ${theme.sz.s6};
    width: 100%;

    @media (max-width: ${theme.sz.xl}) {
      overflow-x: auto;
      scroll-snap-type: x mandatory;
      scrollbar-width: none;
      -ms-overflow-style: none;

      &::-webkit-scrollbar {
        display: none;
      }

      & > * {
        flex-shrink: 0;
        scroll-snap-align: start;
        max-width: ${theme.sz.s40};
      }
    }
  `,
);

export const Banner = styled.div<{ type: 'known' | 'difficult' }>(
  ({ theme, type }) => css`
    display: flex;
    align-items: center;
    padding: ${theme.sz.s2} ${theme.sz.s1};
    border-radius: ${theme.bo.small};
    margin-bottom: ${theme.sz.s4};
    background-color: ${type === 'known' ? theme.co.success98 : theme.co.neutral98};
    color: ${theme.co.secondary20};
    font-size: ${theme.sz.s6};

    path {
      fill: ${type === 'known' ? theme.co.success40 : theme.co.primary20};
    }
  `,
);

export const BannerLabel = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxxs};
    color: ${theme.co.secondary0};
    margin-left: ${theme.sz.s1};
  `,
);

export const BannerHeading = styled.span(
  ({ theme }) => css`
    ${theme.typography.heading.desktop.xxs};
    color: ${theme.co.secondary0};
    z-index: 1;
  `,
);

export const BannerText = styled.span(
  ({ theme }) => css`
    ${theme.typography.paragraph.s};
    color: ${theme.co.secondary30};
  `,
);

export const WordContainer = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.sz.s4};
    border-bottom: ${theme.bo.mediumWidth} solid ${theme.co.neutral90};
    order: 0;
  `,
);

export const TargetLabel = styled.h2(
  ({ theme }) => css`
    margin: 0;
    text-transform: uppercase;
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary20};
  `,
);

export const TargetValue = styled.h2(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s2} 0;
    ${theme.typography.heading.desktop.l};
    color: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.xl}) {
      ${theme.typography.heading.desktop.xxs};
    }
  `,
);

export const SourceLabel = styled.h2(
  ({ theme }) => css`
    margin: 0;
    text-transform: uppercase;
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary20};
  `,
);

export const SourceValue = styled.h2(
  ({ theme }) => css`
    margin: 0 0 ${theme.sz.s2} 0;
    ${theme.typography.paragraph.l};
    color: ${theme.co.secondary20};

    @media (max-width: ${theme.sz.xl}) {
      ${theme.typography.paragraph.m};
    }
  `,
);

export const DetailSection = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.sz.s3};
  `,
);

export const DetailSectionWithEllipsis = styled(DetailSection)(
  () => css`
    position: relative;
  `,
);

export const DetailLabel = styled.span(
  ({ theme }) => css`
    text-transform: uppercase;
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
  `,
);

export const DetailValue = styled.span(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s2};
    ${theme.typography.paragraph.m};
    color: ${theme.co.secondary50};
  `,
);

export const AudioSection = styled.div<{ $hasBorderBottom?: boolean }>(
  ({ theme, $hasBorderBottom }) => css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.sz.s5};
    padding-bottom: ${theme.sz.s6};
    border-bottom: ${
      $hasBorderBottom ? `${theme.bo.mediumWidth} solid ${theme.co.neutral90}` : 'none'
    };
    order: 1;

    @media (max-width: ${theme.sz.xl}) {
      order: 3;
      margin-top: ${theme.sz.s4};
    }
  `,
);

export const VideoSection = styled.div(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.sz.s6};
    padding-bottom: ${theme.sz.s3};
  `,
);

export const AudioLabel = styled(SourceLabel)(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s2};
  `,
);

export const AudioPlayersContainer = styled.div(
  ({ theme }) => css`
    background: ${theme.co.secondary95};
    border-radius: ${theme.bo.medium};
    display: flex;
    padding: ${theme.sz.s2};
    width: fit-content;

    @media (max-width: ${theme.sz.xl}) {
      flex-wrap: wrap;
    }
  `,
);

export const AudioPlayerWrapper = styled.div(() => css``);

export const VideoPlayerContainer = styled.div(
  ({ theme }) => css`
    margin-bottom: ${theme.sz.s3};
    width: 100%;
    max-width: 100%;
    min-width: 0;

    div:first-child {
      width: 100%;
      max-width: 100%;
    }

    video {
      width: 100%;
      max-width: 100%;
      height: auto;
      object-fit: contain;
    }
  `,
);

export const VideoSliderContainer = styled.div(
  ({ theme }) => css`
    .slick-dots {
      display: flex !important;
      justify-content: center;
      padding: 0;
      width: 100%;

      li {
        display: inline-block;
        margin: 0 ${theme.sz.s1};
        cursor: pointer;

        button {
          display: block;
          width: ${theme.sz.s2};
          height: ${theme.sz.s2};
          padding: 0;
          cursor: pointer;
          color: transparent;
          border: 0;
          background: ${theme.co.neutral80};
          border-radius: 50%;
        }

        &.slick-active button {
          background: ${theme.co.primary20};
        }
      }
    }
  `,
);

export const PronunciationBuddyBanner = styled.button(
  ({ theme }) => css`
    text-decoration: none;
    border: none;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: ${theme.sz.s4};
    border-radius: ${theme.sz.s2};
    background-color: ${theme.co.tertiary90};
    margin-top: 0;
    order: 2;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    @media (max-width: ${theme.sz.xl}) {
      order: 4;
    }
  `,
);

export const BuddyImage = styled(Icon)(
  ({ theme }) => css`
    position: absolute;
    top: ${theme.sz.s2};
    right: 0;
    font-size: ${theme.sz.s20};
    transform: rotate(-30deg);
  `,
);

export const MobileVideoSection = styled(VideoSection)(
  ({ theme }) => css`
    order: 2;
    border-bottom: ${theme.bo.mediumWidth} solid ${theme.co.neutral90};

    @media (max-width: ${theme.sz.xl}) {
      display: flex;
      margin: 0;
      border-bottom: none;
    }
  `,
);

export const EllipsisPopout = styled(_EllipsisPopout)(
  ({ theme }) => css`
    font-size: ${theme.sz.s6};
  `,
);

export const HeaderContainer = styled.div(
  ({ theme }) => css`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    margin-bottom: ${theme.sz.s2};
  `,
);

export const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;
  position: relative;
`;

export const BannerContainer = styled.div`
  flex: 1;
`;

export const EllipsisContainer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 10;
`;

export const EllipsisColumn = styled.div<{ $hasBanners?: boolean }>(
  ({ theme }) => css`
    flex: 0 0 auto;
    order: 2;
    margin-top: ${theme.sz.s1};

    @media (max-width: ${theme.sz.xl}) {
      display: none;
    }
  `,
);
